import { startRegistration } from '@simplewebauthn/browser';
import { useEffect, useMemo, useState } from 'react';
import { begin_registration, finish_registration } from '../api/kernel_client';
import { CeremonyChrome } from '../components/ceremony_chrome';
import { get_translations } from '../i18n/translations';
import { probe_passkey_support } from '../lib/capability_probe';
import { read_popup_params } from '../lib/url_params';
import { send_to_opener } from '../protocol/messenger';

type CeremonyState =
  | { kind: 'idle' }
  | { kind: 'running' }
  | { kind: 'success' }
  | { kind: 'error'; message: string };

const CEREMONY_TIMEOUT_MS = 60_000;

/**
 * /popup/register — opened from monitoring or employee's "Add a passkey"
 * UI. Reads URL params for label + authorization-proof type:
 *   ?intent=register&label=<user-chosen>&authorization=<base64-json>
 *
 * The authorization JSON is generated on the opener side from one of:
 *   { type: 'session' }                         (first credential only)
 *   { type: 'assertion', assertion_proof: ... } (re-auth for 2nd+ key)
 *   { type: 'recovery', recovery_token: ... }   (lost-passkey flow)
 *
 * On success the popup posts the new credential_id back; the opener
 * refreshes its passkey list.
 */
export function PopupRegisterRoute() {
  const t = useMemo(() => get_translations(), []);
  const params = useMemo(() => read_popup_params(window.location.search), []);
  const search = useMemo(() => new URLSearchParams(window.location.search), []);
  const [supported, set_supported] = useState<boolean | null>(null);
  const [state, set_state] = useState<CeremonyState>({ kind: 'idle' });
  const [label, set_label] = useState<string>(search.get('label') ?? 'Passkey');

  useEffect(() => {
    void probe_passkey_support().then((ok) => set_supported(ok));
  }, []);

  useEffect(() => {
    if (params === null) return;
    const opener = window.opener;
    if (opener === null) return;
    send_to_opener(opener, params.return_origin, { type: 'ready', request_id: params.request_id });
  }, [params]);

  useEffect(() => {
    if (state.kind !== 'running') return undefined;
    const handle = window.setTimeout(() => {
      if (params !== null) {
        const opener = window.opener;
        if (opener !== null) {
          send_to_opener(opener, params.return_origin, {
            type: 'cancel',
            request_id: params.request_id,
            reason: 'timeout',
          });
        }
      }
      set_state({ kind: 'error', message: t.error_timeout });
    }, CEREMONY_TIMEOUT_MS);
    return () => window.clearTimeout(handle);
  }, [state.kind, params, t.error_timeout]);

  async function run_ceremony() {
    if (params === null) return;
    set_state({ kind: 'running' });
    try {
      const auth_json = search.get('authorization') ?? '';
      let parsed: unknown = { type: 'session' };
      if (auth_json !== '') {
        try {
          parsed = JSON.parse(atob(auth_json));
        } catch {
          parsed = { type: 'session' };
        }
      }
      const opts = await begin_registration({
        label,
        authorization: parsed as Parameters<typeof begin_registration>[0]['authorization'],
      });
      const credential = await startRegistration({
        optionsJSON: opts.public_key as unknown as Parameters<
          typeof startRegistration
        >[0]['optionsJSON'],
      });
      const result = await finish_registration({
        challenge_id: opts.challenge_id,
        credential,
        label,
      });
      const opener = window.opener;
      if (opener !== null) {
        send_to_opener(opener, params.return_origin, {
          type: 'success',
          request_id: params.request_id,
          credential_id: result.credential.id,
        });
      }
      set_state({ kind: 'success' });
      window.setTimeout(() => window.close(), 250);
    } catch (e) {
      const message = e instanceof Error ? e.message : t.error_unknown;
      const cancelled = /cancel|abort/i.test(message);
      const opener = window.opener;
      if (opener !== null && params !== null) {
        if (cancelled === true) {
          send_to_opener(opener, params.return_origin, {
            type: 'cancel',
            request_id: params.request_id,
            reason: 'user_denied',
          });
        } else {
          send_to_opener(opener, params.return_origin, {
            type: 'error',
            request_id: params.request_id,
            code: 'WEBAUTHN_FAILED',
            message,
          });
        }
      }
      set_state({ kind: 'error', message: cancelled === true ? t.error_user_cancelled : message });
    }
  }

  if (params === null) {
    return <CeremonyChrome title={t.app_title} body={<p>Invalid launch parameters.</p>} />;
  }
  if (supported === false) {
    return <CeremonyChrome title={t.app_title} body={<p>{t.error_no_support}</p>} />;
  }
  if (supported === null) {
    return <CeremonyChrome title={t.app_title} body={<p>{t.waiting}</p>} />;
  }

  return (
    <CeremonyChrome
      title={t.app_title}
      body={
        <>
          <label style={{ display: 'block', marginBottom: '12px', color: '#1a1a1a' }}>
            {t.register_label}
            <input
              type="text"
              value={label}
              onChange={(e) => set_label(e.target.value)}
              disabled={state.kind === 'running'}
              style={{
                marginTop: '4px',
                padding: '8px',
                width: '100%',
                borderRadius: '6px',
                border: '1px solid #cccccc',
              }}
            />
          </label>
          {state.kind === 'idle' || state.kind === 'error' ? (
            <button onClick={run_ceremony} style={primary_button_style}>
              {t.register_passkey}
            </button>
          ) : null}
          {state.kind === 'running' ? <p style={{ color: '#545454' }}>{t.waiting}</p> : null}
          {state.kind === 'success' ? <p style={{ color: '#0a7c1f' }}>{t.success}</p> : null}
          {state.kind === 'error' ? (
            <p role="alert" style={{ color: '#b00020' }}>
              {state.message}
            </p>
          ) : null}
        </>
      }
    />
  );
}

const primary_button_style: React.CSSProperties = {
  background: '#BCCF17',
  color: '#1a1a1a',
  border: 'none',
  borderRadius: '8px',
  padding: '12px 24px',
  fontSize: '16px',
  fontWeight: 600,
  cursor: 'pointer',
  width: '100%',
};
